import { useState } from 'react';
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import { Button } from '@tremor/react';
import {
  erasePersonData,
  exportMyData,
  exportPersonData,
} from '../../../network/gdpr';
import { saveBlobResponse } from '../../../utils/saveBlob';
import { apiErrorMessage, notifyError } from '../../../utils/notifyError';
import { toast } from '../../../utils/useToast';

type EraseDialogProps = {
  open: boolean;
  email: string;
  name?: string;
  onClose: () => void;
  onConfirm: (typedEmail: string) => Promise<void>;
};

/** Asks the admin to type the person's email before erasing them. */
export function EraseDataDialog({
  open,
  email,
  name,
  onClose,
  onConfirm,
}: EraseDialogProps) {
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const matches =
    typed.trim().length > 0 &&
    typed.trim().toLowerCase() === (email || '').trim().toLowerCase();

  const close = () => {
    if (submitting) return;
    setTyped('');
    setError(null);
    onClose();
  };

  const confirm = async () => {
    if (!matches) return;
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(typed.trim());
      setTyped('');
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not erase this person’s data.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} className="relative z-20">
      <DialogBackdrop className="fixed inset-0 bg-gray-500 bg-opacity-75" />
      <div className="fixed inset-0 z-20 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
          <DialogTitle as="h3" className="text-base font-semibold text-gray-900">
            Erase personal data{name ? ` for ${name}` : ''}
          </DialogTitle>
          <p className="mt-2 text-sm text-gray-600">
            This replaces their name, email, phone and address with
            placeholders, deletes the documents they uploaded and removes their
            sign-in. Leave records are kept, anonymised, so reports still add
            up. This cannot be undone.
          </p>
          <label className="mt-4 block text-sm text-gray-700">
            Type <span className="font-medium">{email}</span> to confirm
            <input
              type="email"
              aria-label="Confirm email"
              autoComplete="off"
              className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1"
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
            />
          </label>
          {error && (
            <p className="mt-2 text-sm text-red-600" role="alert">
              {error}
            </p>
          )}
          <div className="mt-6 flex justify-end gap-3">
            <Button variant="secondary" onClick={close} disabled={submitting}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={confirm}
              loading={submitting}
              disabled={!matches || submitting}
            >
              Erase data
            </Button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}

type Props = {
  /** The person shown. Omit on your own profile. */
  personId?: string;
  email?: string;
  name?: string;
  isOwnProfile: boolean;
  isAdmin: boolean;
  onErased?: () => void;
};

/**
 * GDPR actions on a profile: anyone can download their own data; admins can
 * export or erase another person's.
 */
export function PersonDataControls({
  personId,
  email,
  name,
  isOwnProfile,
  isAdmin,
  onErased,
}: Props) {
  const [exporting, setExporting] = useState(false);
  const [eraseOpen, setEraseOpen] = useState(false);

  const download = async () => {
    setExporting(true);
    try {
      if (isOwnProfile) {
        saveBlobResponse(await exportMyData(), 'my-data-export.json');
      } else if (personId) {
        saveBlobResponse(
          await exportPersonData(personId),
          `person-${personId}-export.json`,
        );
      }
    } catch (error) {
      notifyError(error, 'Could not export the data.');
    } finally {
      setExporting(false);
    }
  };

  const erase = async (confirmEmail: string) => {
    if (!personId) return;
    await erasePersonData(personId, confirmEmail);
    setEraseOpen(false);
    toast({ variant: 'success', title: 'Personal data erased.' });
    onErased?.();
  };

  const canAdminister = isAdmin && !isOwnProfile && Boolean(personId);
  if (!isOwnProfile && !canAdminister) return null;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-3">
        <Button variant="secondary" loading={exporting} onClick={download}>
          {isOwnProfile ? 'Download my data' : 'Export data'}
        </Button>
        {canAdminister && email && (
          <Button color="red" onClick={() => setEraseOpen(true)}>
            Erase personal data
          </Button>
        )}
      </div>
      {canAdminister && email && (
        <EraseDataDialog
          open={eraseOpen}
          email={email}
          name={name}
          onClose={() => setEraseOpen(false)}
          onConfirm={erase}
        />
      )}
    </div>
  );
}
